import React from 'react';
import BaseChart from './BaseChart';
import { EChartsOption } from 'echarts';

interface PieChartProps {
  data: {
    name: string;
    value: number;
    color?: string;
  }[];
  title?: string;
  className?: string;
  loading?: boolean;
  innerRadius?: string;
  showLegend?: boolean;
  centerText?: string;
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  className,
  loading = false,
  innerRadius = '0%',
  showLegend = true,
  centerText
}) => {
  // Include all items in chart data for legend, but make zero values invisible
  // Use a very small value (0.001) for zero values so they appear in legend but are visually negligible
  const defaultColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
  
  const chartData = data.map((item, index) => {
    const isZero = item.value === 0;
    const itemColor = item.color || defaultColors[index % 5];
    
    return {
      name: item.name,
      // Use a tiny value for zero so it's in the data but visually negligible
      value: isZero ? 0.001 : item.value,
      // Store original value for tooltip/legend display
      _originalValue: item.value,
      itemStyle: {
        // Always use the proper color so legend shows it correctly
        // Keep color fully visible so legend shows the color indicator
        color: itemColor,
        borderColor: isZero ? itemColor : '#ffffff', // Use same color as segment for zero to hide border
        borderWidth: isZero ? 0 : 2,
        // Keep opacity at 1 for zero values so legend color shows, but segment will be tiny due to minAngle
        opacity: 1
      }
    };
  });
  
  // Store color mapping for legend
  const colorMap = data.reduce((acc, item, index) => {
    acc[item.name] = item.color || defaultColors[index % 5];
    return acc;
  }, {} as Record<string, string>);
  
  const option: EChartsOption = {
    title: {
      text: title,
      textStyle: {
        color: '#1f2937',
        fontSize: 16
      },
      top: 10,
      left: 10
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        // Use original value if available, otherwise use displayed value
        const actualValue = params.data._originalValue !== undefined ? params.data._originalValue : params.value;
        // Don't show percentage for zero values
        if (actualValue === 0) {
          return `${params.name}: ${actualValue}`;
        }
        return `${params.name}: ${actualValue} (${params.percent}%)`;
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      textStyle: {
        color: '#ffffff'
      }
    },
    legend: showLegend ? {
      orient: 'vertical',
      right: 5,
      top: 'center',
      textStyle: {
        color: '#1f2937',
        fontSize: 12
      },
      itemGap: 12,
      formatter: function(name: string) {
        const item = data.find(d => d.name === name);
        return `${name}: ${item?.value || 0}`;
      },
      // Disable click to select/deselect since we want to show all items
      selectedMode: false
    } : undefined,
    series: [
      {
        type: 'pie',
        radius: [innerRadius, '65%'], // Outer radius for donut visibility
        center: ['35%', '50%'],
        avoidLabelOverlap: false,
        stillShowZeroSum: true, // Show chart even if sum is zero
        minAngle: 1, // Minimum angle to show segment (hides very small segments like 0.001)
        // Explicitly set colors array to ensure legend shows correct colors for all items
        color: chartData.map(item => {
          // Get the color from itemStyle, which has the proper color even for zero values
          return (item as any).itemStyle?.color || '#1f2937';
        }),
        itemStyle: {
          borderRadius: 2
          // borderColor and borderWidth are set per item in chartData
        },
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
            color: '#1f2937'
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        labelLine: {
          show: false
        },
        data: chartData,
        animation: true,
        animationType: 'scale',
        animationEasing: 'elasticOut',
        // Ensure chart always renders
        silent: false
      }
    ]
  };

  // Add center text for donut charts - matching HTML mockup structure
  // HTML mockup uses two separate divs: donut-value (large, bold) and donut-label (small, light)
  // We'll use two separate graphic elements positioned precisely to match this
  if (centerText && innerRadius !== '0%') {
    const lines = centerText.split('\n');
    if (lines.length >= 2) {
      // Create two separate graphic elements (like the HTML mockup's two divs)
      option.graphic = [
        {
          type: 'text',
          left: '35%', // Match pie chart center position ['35%', '50%']
          top: '47%', // Slightly above center for the value
          style: {
            text: lines[0], // First line: the number (e.g., "45")
            fill: '#1f2937',
            fontSize: 20, // var(--font-size-xl) = 1.25rem = 20px
            fontWeight: 'bold' // font-weight: 600
          },
          z: 100
        },
        {
          type: 'text',
          left: '35%', // Same horizontal position
          top: '55%', // Add a bit more vertical gap below the value
          style: {
            text: lines[1], // Second line: the label (e.g., "Total")
            fill: '#6b7280', // var(--color-text-light)
            fontSize: 12, // var(--font-size-xs) = 0.75rem = 12px
            fontWeight: 'normal'
          },
          z: 100
        }
      ];
    } else if (lines.length === 1) {
      // Fallback: single line
      option.graphic = [
        {
          type: 'text',
          left: '35%',
          top: '50%',
          style: {
            text: lines[0],
            fill: '#1f2937',
            fontSize: 20,
            fontWeight: 'bold'
          },
          z: 100
        }
      ];
    }
  }

  return (
    <BaseChart
      option={option}
      className={className}
      loading={loading}
    />
  );
};

export default PieChart;